import stateTypes from './stateTypes';
import _ from 'lodash';

import {
  getAppInitialState,
  type AppState,
  type GymDashboardData,
  type TrainerDashboardData,
} from './appInitialState';
import {
  getSelectedGymId,
  upsertTrainerDashboard,
  withGymDashboard,
  withTrainerDashboard,
} from './stateHelpers';
import type { Manager, Member, Trainer } from '../types';

import type { AnyAction } from 'redux';

const findTrainerIdsForMember = (
  state: AppState,
  gymId: string,
  memberId: string,
): string[] => {
  const dashboard = state.gymDataMap[gymId];
  if (!dashboard) {
    return [];
  }
  return Object.entries(dashboard.trainers)
    .filter(([, trainerDashboard]) =>
      Boolean(trainerDashboard.members[memberId]),
    )
    .map(([trainerId]) => trainerId);
};

const addIds = (
  existingIds: string[] | undefined,
  idsToAdd: (string | undefined)[],
): string[] => {
  const set = new Set(existingIds ?? []);
  idsToAdd.forEach((id) => {
    if (id) {
      set.add(id);
    }
  });
  return Array.from(set);
};

const addId = (
  existingIds: string[] | undefined,
  id: string | undefined,
): string[] => addIds(existingIds, [id]);

const removeId = (existingIds: string[] | undefined, idToRemove: string) =>
  (existingIds ?? []).filter((candidate) => candidate !== idToRemove);

const withTrainerDashboardsBatch = (
  state: AppState,
  gymId: string,
  trainerIds: string[] | undefined,
  updater: (
    trainerDashboard: TrainerDashboardData,
    trainerId: string,
  ) => TrainerDashboardData,
): AppState => {
  if (!trainerIds?.length) {
    return state;
  }
  return trainerIds.reduce<AppState>(
    (nextState, trainerId) =>
      withTrainerDashboard(nextState, gymId, trainerId, (dashboard) =>
        updater(dashboard, trainerId),
      ),
    state,
  );
};

const removeMemberFromState = (
  state: AppState,
  gymId: string,
  memberId: string,
  trainerIdsHint?: string[],
): AppState => {
  const trainerIds =
    trainerIdsHint && trainerIdsHint.length
      ? trainerIdsHint.filter(Boolean)
      : findTrainerIdsForMember(state, gymId, memberId);

  const stateWithoutMember = withTrainerDashboardsBatch(
    state,
    gymId,
    trainerIds,
    (trainerDashboard) => {
      const { [memberId]: _removed, ...remainingMembers } =
        trainerDashboard.members;
      return {
        ...trainerDashboard,
        trainer: {
          ...trainerDashboard.trainer,
          memberIds: removeId(trainerDashboard.trainer.memberIds, memberId),
        },
        members: remainingMembers,
      };
    },
  );

  return withGymDashboard(stateWithoutMember, gymId, (dashboard) => {
    if (!dashboard) {
      return dashboard;
    }
    const { [memberId]: _removedUnassigned, ...remainingUnassigned } =
      dashboard.unassignedMembers;
    return {
      ...dashboard,
      gym: {
        ...dashboard.gym,
        memberIds: removeId(dashboard.gym.memberIds, memberId),
      },
      unassignedMembers: remainingUnassigned,
    };
  });
};

const removeTrainerDashboard = (
  state: AppState,
  gymId: string,
  trainerId: string,
): AppState => {
  return withGymDashboard(state, gymId, (dashboard) => {
    if (!dashboard) {
      return dashboard;
    }
    const trainerDashboard = dashboard.trainers[trainerId];
    if (!trainerDashboard) {
      return dashboard;
    }
    const { [trainerId]: _removed, ...remainingTrainers } = dashboard.trainers;
    const promotedMembers = { ...dashboard.unassignedMembers };
    Object.values(trainerDashboard.members).forEach((member) => {
      const normalizedMember: Member = {
        ...member,
        trainerIds: (member.trainerIds ?? []).filter(
          (candidate) => candidate !== trainerId,
        ),
      };
      promotedMembers[normalizedMember.id] = normalizedMember;
    });
    return {
      ...dashboard,
      gym: {
        ...dashboard.gym,
        trainerIds: removeId(dashboard.gym.trainerIds, trainerId),
      },
      trainers: remainingTrainers,
      unassignedMembers: promotedMembers,
    };
  });
};

export default function stateReducer(
  state: AppState = getAppInitialState(),
  action: AnyAction,
): AppState {
  switch (action.type) {
    case stateTypes.RESET_ALL_STATE:
      return {
        ...getAppInitialState(),
      };
    case stateTypes.SET_EXERCISES:
      return {
        ...state,
        exercises: action.exercises,
      };
    case stateTypes.SET_MUSCLES:
      return {
        ...state,
        muscles: action.muscles,
      };
    case stateTypes.SET_SELECTED_GYM_ID:
      return {
        ...state,
        selectedGymId: action.selectedGymId,
      };
    case stateTypes.SET_LOGIN_MANAGER_ID:
      return {
        ...state,
        loginManagerId: action.loginManagerId,
      };
    case stateTypes.SET_LOGIN_TRAINER_ID:
      return {
        ...state,
        loginTrainerId: action.loginTrainerId,
      };
    case stateTypes.SET_LOGIN_MEMBER_ID:
      return {
        ...state,
        loginMemberId: action.loginMemberId,
      };
    case stateTypes.SET_GYM_DASHBOARD_DATA:
      return {
        ...state,
        gymDataMap: {
          ...state.gymDataMap,
          [action.gymDashboardData.gym.id]: action.gymDashboardData,
        },
      };
    case stateTypes.SET_EXERCISE: {
      const gymId = getSelectedGymId(state);
      const trainerId = action.exercise.trainerId;
      if (!trainerId) {
        return state;
      }
      return withTrainerDashboard(
        state,
        gymId,
        trainerId,
        (trainerDashboard) => ({
          ...trainerDashboard,
          trainer: {
            ...trainerDashboard.trainer,
            exerciseIds: addId(
              trainerDashboard.trainer.exerciseIds,
              action.exercise.id,
            ),
          },
          personalizedExercises: {
            ...trainerDashboard.personalizedExercises,
            [action.exercise.id]: action.exercise,
          },
        }),
      );
    }
    case stateTypes.SET_REMOVED_EXERCISE: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboard(
        state,
        gymId,
        action.trainerId,
        (trainerDashboard) => {
          const exerciseToUpdate =
            trainerDashboard.personalizedExercises[action.exerciseId];
          if (!exerciseToUpdate) {
            return trainerDashboard;
          }
          return {
            ...trainerDashboard,
            personalizedExercises: {
              ...trainerDashboard.personalizedExercises,
              [action.exerciseId]: {
                ...exerciseToUpdate,
                isDeleted: true,
              },
            },
          };
        },
      );
    }
    case stateTypes.SET_REMOVED_GROUP: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboard(
        state,
        gymId,
        action.trainerId,
        (trainerDashboard) => {
          const { [action.groupId]: _removed, ...remainingGroups } =
            trainerDashboard.groups;
          return {
            ...trainerDashboard,
            trainer: {
              ...trainerDashboard.trainer,
              groupIds: removeId(
                trainerDashboard.trainer.groupIds,
                action.groupId,
              ),
            },
            groups: remainingGroups,
          };
        },
      );
    }
    case stateTypes.SET_REMOVED_PROGRAM: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboard(
        state,
        gymId,
        action.trainerId,
        (trainerDashboard) => {
          const { [action.programId]: _removed, ...remainingPrograms } =
            trainerDashboard.programs;
          return {
            ...trainerDashboard,
            trainer: {
              ...trainerDashboard.trainer,
              programIds: removeId(
                trainerDashboard.trainer.programIds,
                action.programId,
              ),
            },
            programs: remainingPrograms,
          };
        },
      );
    }
    case stateTypes.SET_REMOVED_TEMPLATE: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboard(
        state,
        gymId,
        action.trainerId,
        (trainerDashboard) => {
          const { [action.templateId]: _removed, ...remainingTemplates } =
            trainerDashboard.templates;
          return {
            ...trainerDashboard,
            trainer: {
              ...trainerDashboard.trainer,
              trainingRoutineTemplateIds: removeId(
                trainerDashboard.trainer.trainingRoutineTemplateIds,
                action.templateId,
              ),
            },
            templates: remainingTemplates,
          };
        },
      );
    }
    case stateTypes.SET_GROUP: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboardsBatch(
        state,
        gymId,
        action.group.trainerIds ?? [],
        (trainerDashboard) => ({
          ...trainerDashboard,
          trainer: {
            ...trainerDashboard.trainer,
            groupIds: addId(trainerDashboard.trainer.groupIds, action.group.id),
          },
          groups: {
            ...trainerDashboard.groups,
            [action.group.id]: action.group,
          },
        }),
      );
    }
    case stateTypes.SET_MANAGERS: {
      const gymId = getSelectedGymId(state);
      const managersToUpdate: Manager[] = action.managers;
      if (!managersToUpdate?.length) {
        return state;
      }
      return withGymDashboard(state, gymId, (dashboard) => {
        const updatedManagers = {
          ...dashboard.managers,
          ..._.keyBy(managersToUpdate, 'id'),
        };
        return {
          ...dashboard,
          gym: {
            ...dashboard.gym,
            managerIds: addIds(
              dashboard.gym.managerIds,
              managersToUpdate.map(({ id }) => id),
            ),
          },
          managers: updatedManagers,
        };
      });
    }
    case stateTypes.SET_REMOVED_MANAGER: {
      const gymId = getSelectedGymId(state);
      return withGymDashboard(state, gymId, (dashboard) => {
        const { [action.managerId]: _removed, ...remainingManagers } =
          dashboard.managers;
        return {
          ...dashboard,
          gym: {
            ...dashboard.gym,
            managerIds: removeId(dashboard.gym.managerIds, action.managerId),
          },
          managers: remainingManagers,
        };
      });
    }
    case stateTypes.SET_TRAINERS: {
      const gymId = getSelectedGymId(state);
      const trainersToUpdate: Trainer[] = action.trainers;
      if (!trainersToUpdate?.length) {
        return state;
      }
      const [activeTrainers, inactiveTrainers] = _.partition(
        trainersToUpdate,
        ({ isActive }) => isActive !== false,
      );
      let nextState = inactiveTrainers.reduce<AppState>(
        (acc, trainer) => removeTrainerDashboard(acc, gymId, trainer.id),
        state,
      );
      if (!activeTrainers.length) {
        return nextState;
      }
      return withGymDashboard(nextState, gymId, (dashboard) => {
        let nextDashboard = dashboard;
        for (const trainer of activeTrainers) {
          nextDashboard = upsertTrainerDashboard(nextDashboard, trainer);
        }
        return {
          ...nextDashboard,
          gym: {
            ...nextDashboard.gym,
            trainerIds: addIds(
              nextDashboard.gym.trainerIds,
              activeTrainers.map(({ id }) => id),
            ),
          },
        };
      });
    }
    case stateTypes.SET_MEMBERS: {
      const gymId = getSelectedGymId(state);
      const membersToUpdate: Member[] = action.members;
      if (!membersToUpdate?.length) {
        return state;
      }
      const [activeMembers, inactiveMembers] = _.partition(
        membersToUpdate,
        ({ isActive }) => isActive !== false,
      );
      let nextState = inactiveMembers.reduce<AppState>(
        (acc, member) =>
          removeMemberFromState(acc, gymId, member.id, member.trainerIds),
        state,
      );
      activeMembers.forEach((member) => {
        const trainerIds = (member.trainerIds ?? []).filter(Boolean);
        if (trainerIds.length) {
          nextState = withTrainerDashboardsBatch(
            nextState,
            gymId,
            trainerIds,
            (trainerDashboard) => ({
              ...trainerDashboard,
              trainer: {
                ...trainerDashboard.trainer,
                memberIds: addId(trainerDashboard.trainer.memberIds, member.id),
              },
              members: {
                ...trainerDashboard.members,
                [member.id]: member,
              },
            }),
          );
          nextState = withGymDashboard(nextState, gymId, (dashboard) => {
            if (!dashboard) {
              return dashboard;
            }
            if (!dashboard.unassignedMembers[member.id]) {
              return dashboard;
            }
            const { [member.id]: _removed, ...remainingUnassigned } =
              dashboard.unassignedMembers;
            return {
              ...dashboard,
              unassignedMembers: remainingUnassigned,
            };
          });
        } else {
          nextState = withGymDashboard(nextState, gymId, (dashboard) => {
            if (!dashboard) {
              return dashboard;
            }
            return {
              ...dashboard,
              unassignedMembers: {
                ...dashboard.unassignedMembers,
                [member.id]: member,
              },
            };
          });
        }
      });
      const activeMemberIds = activeMembers.map(({ id }) => id);
      if (!activeMemberIds.length) {
        return nextState;
      }
      return withGymDashboard(nextState, gymId, (dashboard) => ({
        ...dashboard,
        gym: {
          ...dashboard.gym,
          memberIds: addIds(dashboard.gym.memberIds, activeMemberIds),
        },
      }));
    }
    case stateTypes.SET_REMOVED_MEMBER: {
      const gymId = getSelectedGymId(state);
      const { memberId, trainerIds } = action;
      return removeMemberFromState(state, gymId, memberId, trainerIds);
    }
    case stateTypes.SET_REMOVED_TRAINER: {
      const gymId = getSelectedGymId(state);
      return removeTrainerDashboard(state, gymId, action.trainerId);
    }
    case stateTypes.SET_GYM: {
      const existingDashboard = state.gymDataMap[action.gym.id];
      const nextDashboard: GymDashboardData = existingDashboard
        ? {
            ...existingDashboard,
            gym: action.gym,
          }
        : {
            gym: action.gym,
            managers: {},
            trainers: {},
            unassignedMembers: {},
            gymProducts: [],
          };
      return {
        ...state,
        gymDataMap: {
          ...state.gymDataMap,
          [action.gym.id]: nextDashboard,
        },
      };
    }
    case stateTypes.SET_PROGRAM: {
      const gymId = getSelectedGymId(state);
      const trainerIdForProgram = action.program.trainerId;
      if (!trainerIdForProgram) {
        return state;
      }
      return withTrainerDashboard(
        state,
        gymId,
        trainerIdForProgram,
        (trainerDashboard) => {
          return {
            ...trainerDashboard,
            trainer: {
              ...trainerDashboard.trainer,
              programIds: addId(
                trainerDashboard.trainer.programIds,
                action.program.id,
              ),
            },
            programs: {
              ...trainerDashboard.programs,
              [action.program.id]: action.program,
            },
          };
        },
      );
    }
    case stateTypes.SET_TEMPLATE: {
      const gymId = getSelectedGymId(state);
      return withTrainerDashboardsBatch(
        state,
        gymId,
        action.template.trainerIds ?? [],
        (trainerDashboard) => ({
          ...trainerDashboard,
          trainer: {
            ...trainerDashboard.trainer,
            trainingRoutineTemplateIds: addId(
              trainerDashboard.trainer.trainingRoutineTemplateIds,
              action.template.id,
            ),
          },
          templates: {
            ...trainerDashboard.templates,
            [action.template.id]: action.template,
          },
        }),
      );
    }
    case stateTypes.SET_GYM_PRODUCTS: {
      return withGymDashboard(state, action.gymId, (dashboard) => ({
        ...dashboard,
        gymProducts: action.gymProducts,
      }));
    }
    default:
      return state;
  }
}
